import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Check, Plus, Loader2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { fetchUserGroups, createGroup } from '@/lib/supabase-queries';
import { useToast } from '@/hooks/use-toast';

interface GroupSwitcherProps {
  currentGroupId: string;
  currentGroupName: string;
}

interface GroupItem {
  id: string;
  name: string;
}

const GroupSwitcher = ({ currentGroupId, currentGroupName }: GroupSwitcherProps) => {
  const { user, displayName, avatarId } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [groups, setGroups] = useState<GroupItem[]>([]);
  const loaded = useRef(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!user || loaded.current) return;
    loaded.current = true;
    fetchUserGroups(user.id).then((fetched) => {
      setGroups(fetched.map((g) => ({ id: g.id, name: g.name })));
    });
  }, [user]);

  const handleCreateGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newGroupName.trim() || !user) return;
    setCreating(true);
    try {
      const newGroup = await createGroup(newGroupName.trim(), user.id, displayName || 'משתמש', avatarId);
      toast({ title: 'הקבוצה נוצרה! 🎉' });
      setNewGroupName('');
      setDialogOpen(false);
      if (newGroup?.id) {
        navigate(`/group/${newGroup.id}`);
      }
    } catch (err: any) {
      toast({ title: 'שגיאה', description: err.message, variant: 'destructive' });
    }
    setCreating(false);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-1 font-bold text-sm truncate max-w-[180px] hover:text-foreground/80 transition-colors min-h-[44px]">
            <span className="truncate">{currentGroupName}</span>
            <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="bg-card border-border min-w-[200px]"
          dir="rtl"
        >
          {groups.map((group) => (
            <DropdownMenuItem
              key={group.id}
              onClick={() => {
                if (group.id !== currentGroupId) {
                  navigate(`/group/${group.id}`);
                }
              }}
              className={`flex items-center justify-between gap-2 cursor-pointer ${
                group.id === currentGroupId ? 'text-accent' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <span className="truncate">{group.name}</span>
              {group.id === currentGroupId && <Check className="w-4 h-4 flex-shrink-0" />}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => setDialogOpen(true)}
            className="flex items-center gap-2 cursor-pointer text-muted-foreground hover:text-foreground text-xs"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>צור קבוצה חדשה</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent dir="rtl">
          <DialogHeader>
            <DialogTitle>צור קבוצה חדשה</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateGroup} className="flex flex-col gap-3 mt-2">
            <Input
              placeholder="שם הקבוצה"
              value={newGroupName}
              onChange={e => setNewGroupName(e.target.value)}
              disabled={creating}
              autoFocus
            />
            <Button type="submit" disabled={creating || !newGroupName.trim()}>
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'צור קבוצה'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GroupSwitcher;
